import { BlankImg } from 'assets/images';
import React, { useEffect, useMemo, useState } from 'react';
import { getBadgeHostLeaderboard } from 'Services/badgeAnalyticsService';

const COLUMNS = [
  { key: 'host', label: 'Host', sortKey: null },
  { key: 'impressions', label: 'Impressions', sortKey: 'impressions' },
  { key: 'clicks', label: 'Clicks', sortKey: 'clicks' },
  { key: 'ctr', label: 'CTR %', sortKey: 'ctr_pct' },
  { key: 'group_meetings', label: 'Group Meetings', sortKey: 'group_meetings' },
  { key: 'revenue', label: '$ Revenue', sortKey: 'extra_guest_revenue' },
];

const SortIndicator = ({ active, direction }) => {
  if (!active) return <span style={{ color: '#ccc' }}> ▲</span>;
  return <span>{direction === 'asc' ? ' ▲' : ' ▼'}</span>;
};

const SkeletonRow = () => (
  <tr>
    {COLUMNS.map((c) => (
      <td key={c.key}>
        <div
          style={{
            height: '1rem',
            background: '#e0e0e0',
            borderRadius: 4,
          }}
        />
      </td>
    ))}
  </tr>
);

const BadgeHostLeaderboard = ({ storeId, range }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('impressions');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    if (!storeId) return;
    setLoading(true);
    getBadgeHostLeaderboard({ storeId, range, limit: 20 })
      .then((res) => setRows(res?.data?.data || res?.data || []))
      .catch((err) => {
        console.error('BadgeHostLeaderboard fetch error:', err);
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, [storeId, range]);

  const handleSort = (key) => {
    if (!key) return;
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const av = a[sortKey] ?? 0;
      const bv = b[sortKey] ?? 0;
      return sortDir === 'asc' ? av - bv : bv - av;
    });
  }, [rows, sortKey, sortDir]);

  return (
    <div className="column-wrapper p-3 mt-3">
      <p className="chart-main-title">Host Leaderboard</p>
      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr>
              {COLUMNS.map((c) => (
                <th
                  key={c.key}
                  style={{ cursor: c.sortKey ? 'pointer' : 'default', userSelect: 'none' }}
                  onClick={() => handleSort(c.sortKey)}
                >
                  {c.label}
                  {c.sortKey && (
                    <SortIndicator
                      active={sortKey === c.sortKey}
                      direction={sortDir}
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: 3 }).map((_, i) => <SkeletonRow key={i} />)
              : sorted.length === 0
              ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center text-muted">
                    No host data for this period.
                  </td>
                </tr>
              )
              : sorted.map((row, i) => {
                  const ctr =
                    row.ctr_pct != null
                      ? `${Number(row.ctr_pct).toFixed(1)}%`
                      : row.impressions > 0
                      ? `${((row.clicks / row.impressions) * 100).toFixed(1)}%`
                      : '—';
                  const revenue = row.extra_guest_revenue ?? row.revenue ?? 0;
                  return (
                    <tr key={row.host_id || i}>
                      <td className="image-title-wrapper">
                        <img
                          className="host-pp-small"
                          src={row.avatar_url || row.avatar || BlankImg}
                          alt={row.host_name || 'host'}
                          onError={(e) => { e.target.src = BlankImg; }}
                        />{' '}
                        <span>{row.host_name || '—'}</span>
                      </td>
                      <td>{(row.impressions ?? 0).toLocaleString()}</td>
                      <td>{(row.clicks ?? 0).toLocaleString()}</td>
                      <td>{ctr}</td>
                      <td>{(row.group_meetings ?? 0).toLocaleString()}</td>
                      <td>${Number(revenue).toFixed(2)}</td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BadgeHostLeaderboard;
